import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ClipboardList,
  Clock3,
  Compass,
  Copy,
  HeartHandshake,
  LifeBuoy,
  Megaphone,
  MessageCircle,
  RefreshCw,
  Send,
  ShieldCheck,
  Sprout,
  Users,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ambassadors } from "../services/ambassadors";
import ConfirmDialog, {
  AmbassadorConfirmation,
} from "../components/ambassadors/ConfirmDialog";
import {
  AmbassadorContext,
  AmbassadorHistoryKind,
  AmbassadorMaterial,
  AmbassadorProfile,
  AmbassadorResources,
  ambassadorDate,
  ambassadorDay,
  ambassadorError,
  ambassadorPerson,
  ambassadorStatuses,
  materialKinds,
} from "../types/ambassadors";
import "./Ambassadors.css";

type Tab = "onboarding" | "content" | "missions" | "reports" | "support";
type ReportDraft = {
  summary: string;
  publishedCount: string;
  meaningfulInteractions: string;
  dailyChecklist: string[];
  missionId: string;
  evidenceUrl: string;
};
const emptyDraft: ReportDraft = {
  summary: "",
  publishedCount: "0",
  meaningfulInteractions: "0",
  dailyChecklist: [],
  missionId: "",
  evidenceUrl: "",
};
const tabs = [
  { key: "onboarding", label: "ورود و آموزش", icon: BookOpen },
  { key: "content", label: "محتوا و اطلاعیه", icon: Megaphone },
  { key: "missions", label: "مأموریت‌ها", icon: Compass },
  { key: "reports", label: "گزارش فعالیت", icon: ClipboardList },
  { key: "support", label: "پشتیبانی", icon: LifeBuoy },
] as const;

export default function Ambassadors() {
  const { user, loading: authLoading } = useAuth();
  const [context, setContext] = useState<AmbassadorContext>();
  const [profile, setProfile] = useState<AmbassadorProfile>();
  const [resources, setResources] = useState<AmbassadorResources>({
    materials: [],
    missions: [],
  });
  const [tab, setTab] = useState<Tab>("onboarding");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [consentRead, setConsentRead] = useState(false);
  const [confirmation, setConfirmation] =
    useState<AmbassadorConfirmation | null>(null);
  const [draft, setDraft] = useState<ReportDraft>(emptyDraft);
  const [draftOwner, setDraftOwner] = useState("");
  const request = useRef(0);
  const initialized = useRef(false);
  const invalidateRequests = useCallback(() => {
    request.current++;
  }, []);
  const feedback = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const mutation = useRef(false);
  const draftKey = user ? `ambassador-report:${user.id}` : "";

  const load = useCallback(async () => {
    const version = ++request.current;
    const ctx = await ambassadors.context();
    const [p, r] = ctx.profile
      ? await Promise.all([ambassadors.me(), ambassadors.resources()])
      : [undefined, { materials: [], missions: [] } as AmbassadorResources];
    if (version !== request.current) return;
    setContext(ctx);
    setProfile(p);
    setResources(r);
    setDraft((d) =>
      d.missionId &&
      !r.missions.some(
        (m) =>
          m.id === d.missionId &&
          (!m.deadline || new Date(m.deadline) >= new Date()),
      )
        ? { ...d, missionId: "" }
        : d,
    );
    if (!initialized.current && p) {
      setTab(
        ["WITHDRAWN", "DECLINED"].includes(p.status)
          ? "support"
          : p.activatedAt
            ? "reports"
            : "onboarding",
      );
      initialized.current = true;
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    let active = true;
    setLoading(true);
    setError("");
    setProfile(undefined);
    setContext(undefined);
    initialized.current = false;
    load()
      .catch((e) => {
        if (active) setError(ambassadorError(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      invalidateRequests();
    };
  }, [user, load, invalidateRequests]);
  useEffect(() => {
    if (!draftKey) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(draftKey) || "null");
      setDraft(
        saved &&
          typeof saved.summary === "string" &&
          Array.isArray(saved.dailyChecklist)
          ? { ...emptyDraft, ...saved }
          : emptyDraft,
      );
    } catch {
      setDraft(emptyDraft);
    }
    setDraftOwner(draftKey);
  }, [draftKey]);
  useEffect(() => {
    if (!draftKey || draftOwner !== draftKey) return;
    try {
      sessionStorage.setItem(draftKey, JSON.stringify(draft));
    } catch {
      /* A disabled storage setting must not prevent reporting. */
    }
  }, [draft, draftKey, draftOwner]);

  const run = async (action: () => Promise<unknown>, onSaved?: () => void) => {
    if (mutation.current) return false;
    mutation.current = true;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      await action();
      onSaved?.();
      setSuccess("اطلاعات شما با موفقیت ثبت شد.");
      try {
        await load();
      } catch {
        setError(
          "اطلاعات ثبت شد، اما دریافت آخرین وضعیت ممکن نشد. صفحه را تازه کنید؛ نیازی به ارسال دوباره نیست.",
        );
      }
      return true;
    } catch (e) {
      setError(ambassadorError(e));
      return false;
    } finally {
      mutation.current = false;
      setBusy(false);
      feedback.current?.focus();
    }
  };
  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      await load();
    } catch (e) {
      setError(ambassadorError(e));
    } finally {
      setLoading(false);
    }
  };
  const copy = async (body: string) => {
    try {
      await navigator.clipboard.writeText(body);
      setSuccess("متن کپی شد. پیش از انتشار آن را بررسی کنید.");
    } catch {
      setError("کپی خودکار در این مرورگر ممکن نیست؛ متن را انتخاب و کپی کنید.");
    }
  };
  const moveToTab = (nextTab: Tab) => {
    setTab(nextTab);
    window.requestAnimationFrame(() => {
      panel.current?.focus({ preventScroll: true });
      panel.current?.scrollIntoView({
        block: "start",
        behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)")
          .matches
          ? "auto"
          : "smooth",
      });
    });
  };
  const more = async (kind: AmbassadorHistoryKind) => {
    if (!profile || mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setError("");
    try {
      const rows = await ambassadors.history(
        kind,
        (profile[kind] || []).length,
      );
      setProfile((p) =>
        p ? { ...p, [kind]: [...(p[kind] || []), ...rows] } : p,
      );
    } catch (e) {
      setError(ambassadorError(e));
    } finally {
      mutation.current = false;
      setBusy(false);
    }
  };
  const loadMore = (kind: "reports" | "tickets") =>
    profile &&
    (profile[kind]?.length || 0) < (profile.historyCounts?.[kind] || 0) ? (
      <button
        disabled={busy}
        className="amb-secondary"
        onClick={() => more(kind)}
      >
        نمایش موارد قدیمی‌تر
      </button>
    ) : null;
  const resourceCard = (m: AmbassadorMaterial) => (
    <article className="amb-card amb-resource" data-kind={m.kind} key={m.id}>
      <div className="amb-resource-heading">
        <span className="amb-resource-icon" aria-hidden="true">
          {["TRAINING", "REFERENCE"].includes(m.kind) ? (
            <BookOpen size={22} />
          ) : (
            <Megaphone size={22} />
          )}
        </span>
        <span className="amb-badge">{materialKinds[m.kind]}</span>
      </div>
      <h2>{m.title}</h2>
      <p className="amb-pre">{m.body}</p>
      <div className="amb-row">
        {m.url && (
          <a
            className="amb-button"
            href={m.url}
            target="_blank"
            rel="noreferrer"
          >
            مشاهده لینک <ArrowLeft size={16} />
          </a>
        )}
        <button
          className="amb-secondary"
          type="button"
          onClick={() => copy(m.body)}
        >
          <Copy size={16} /> کپی متن
        </button>
      </div>
    </article>
  );
  if (authLoading)
    return (
      <div className="amb amb-member amb-auth-loading" role="status">
        <span className="amb-loading-icon">
          <HeartHandshake size={28} />
        </span>
        در حال بررسی حساب کاربری…
      </div>
    );
  if (!user) return <Navigate to="/login?next=/ambassadors" replace />;
  const stopped =
    !!profile && ["WITHDRAWN", "DECLINED"].includes(profile.status);
  const canReport =
    !!profile?.activatedAt &&
    ["ACTIVE", "NEEDS_SUPPORT"].includes(profile!.status);
  const required = context?.onboarding.filter((item) => item.required) || [];
  const checked = required.filter((item) =>
    profile?.checklist.some((c) => c.key === item.key && c.completed),
  ).length;
  const next = required.find(
    (item) =>
      !profile?.checklist.some((c) => c.key === item.key && c.completed),
  );
  const activeMissions = resources.missions.filter(
    (m) => !m.deadline || new Date(m.deadline) >= new Date(),
  );
  const openTickets =
    profile?.tickets?.filter((t) => !t.resolvedAt).length || 0;
  const reportCount =
    profile?.historyCounts?.reports ?? profile?.reports?.length ?? 0;
  const reportCountLabel =
    profile?.historyCounts?.reports == null
      ? "گزارش‌های اخیر"
      : "گزارش ثبت‌شده";
  return (
    <main className="amb amb-member" aria-busy={busy || loading}>
      <nav className="amb-breadcrumb" aria-label="مسیر دسترسی">
        <Link to="/">خانه</Link>
        <ChevronLeft size={13} aria-hidden="true" />
        <Link to="/dashboard">حساب کاربری</Link>
        <ChevronLeft size={13} aria-hidden="true" />
        <span>فضای سفیران</span>
      </nav>
      <header className="amb-hero">
        <div className="amb-hero-copy">
          <span className="amb-eyebrow">
            <HeartHandshake size={16} /> همراهی کوچک، اثر ماندگار
          </span>
          <h1>فضای سفیران من حقیقی</h1>
          <p>
            محتوای مناسب را انتخاب کنید، تجربه‌تان را به اشتراک بگذارید و همراه
            پشتیبان قدم بردارید.
          </p>
        </div>
        <div className="amb-hero-actions">
          <button
            className="amb-secondary amb-refresh"
            onClick={refresh}
            disabled={busy || loading}
            aria-label="به‌روزرسانی پرونده"
          >
            <RefreshCw size={16} className={loading ? "amb-spin" : ""} />{" "}
            به‌روزرسانی
          </button>
          <Link className="amb-button amb-secondary" to="/dashboard">
            حساب کاربری <ArrowLeft size={16} />
          </Link>
        </div>
      </header>
      <div ref={feedback} tabIndex={-1} className="amb-feedback">
        {error && (
          <div role="alert" className="amb-error">
            <span>{error}</span>
            <button
              disabled={busy || loading}
              className="amb-secondary"
              onClick={refresh}
            >
              تازه‌سازی اطلاعات
            </button>
          </div>
        )}
        {success && (
          <div role="status" className="amb-success">
            <CheckCircle2 size={18} />
            <span>{success}</span>
            <button
              type="button"
              className="amb-icon-button"
              aria-label="بستن پیام"
              onClick={() => setSuccess("")}
            >
              ×
            </button>
          </div>
        )}
      </div>
      {loading && !profile ? (
        <div
          className="amb-skeleton"
          role="status"
          aria-label="در حال دریافت پرونده"
        >
          <div />
          <div />
          <div />
        </div>
      ) : !profile ? (
        <section className="amb-card amb-empty">
          <Users size={36} />
          <h2>
            {error ? "دریافت پرونده انجام نشد" : "همراه مسیر آگاهی باشید"}
          </h2>
          <p>
            {error
              ? "پس از برقراری ارتباط، اطلاعات را تازه کنید."
              : "برای پیوستن به جمع سفیران من حقیقی، درخواست خود را آنلاین ثبت کنید. تیم جذب اطلاعات شما را بررسی می‌کند و نتیجه را در صفحه درخواست می‌بینید."}
          </p>
          <div className="amb-row justify-center">
            {!error && (
              <Link className="amb-button" to="/ambassadors/join">
                درخواست سفیر شدن <ArrowLeft size={16} />
              </Link>
            )}
            <Link className="amb-button amb-secondary" to="/contact">
              راه‌های ارتباط با مجموعه
            </Link>
          </div>
        </section>
      ) : (
        <>
          <section className="amb-card amb-profile-summary">
            <div className="amb-profile-identity">
              <div className="amb-avatar" aria-hidden="true">
                {profile.fullName.trim().slice(0, 1)}
              </div>
              <div>
                <span className="amb-muted amb-profile-label">
                  پرونده سفیری شما
                </span>
                <h2>{profile.fullName}</h2>
                <span className="amb-badge" data-status={profile.status}>
                  {ambassadorStatuses[profile.status]}
                </span>
              </div>
            </div>
            <dl className="amb-summary-grid">
              <div>
                <dt>
                  <LifeBuoy size={14} /> همراه شما
                </dt>
                <dd>{ambassadorPerson(profile.support)}</dd>
              </div>
              <div>
                <dt>
                  <Clock3 size={14} /> آخرین گزارش
                </dt>
                <dd>{ambassadorDate(profile.lastActivityAt)}</dd>
              </div>
            </dl>
          </section>
          <div className="amb-metrics" aria-label="نمای کلی فعالیت شما">
            {[
              {
                label: reportCountLabel,
                value: reportCount,
                icon: ClipboardList,
              },
              {
                label: "محتوای در دسترس",
                value: resources.materials.length,
                icon: BookOpen,
              },
              {
                label: "مأموریت باز",
                value: activeMissions.length,
                icon: Compass,
              },
              {
                label: "اعضای واقعی کانال",
                value: profile.memberCount,
                icon: Users,
              },
            ].map((metric) => (
              <article className="amb-metric" key={metric.label}>
                <span className="amb-metric-icon">
                  <metric.icon size={21} />
                </span>
                <div>
                  <strong>{metric.value.toLocaleString("fa-IR")}</strong>
                  <span>{metric.label}</span>
                </div>
              </article>
            ))}
          </div>
          {!stopped && !profile.activatedAt && (
            <aside className="amb-next">
              <span className="amb-next-icon" aria-hidden="true">
                <Sprout size={26} />
              </span>
              <div className="amb-next-copy">
                <span className="amb-eyebrow">قدم بعدی شما</span>
                <h2>
                  {profile.status === "RECRUITING"
                    ? "با برنامه آشنا شوید و تصمیم بگیرید"
                    : !profile.channelUrl
                      ? "اطلاعات کانال خود را ثبت کنید"
                      : next
                        ? next.title
                        : "منتظر تأیید نهایی پشتیبان باشید"}
                </h2>
                <p>
                  {profile.status === "RECRUITING"
                    ? "پذیرش همکاری آگاهانه و داوطلبانه است."
                    : "پشتیبان مراحل انجام‌شده را تأیید می‌کند؛ رسیدن به ۱۰۰ عضو شرط فعال‌سازی نیست."}
                </p>
                {required.length > 0 && (
                  <div className="amb-readiness">
                    <progress
                      value={checked}
                      max={required.length}
                      aria-label="آمادگی شروع همکاری"
                    />
                    <span>
                      {checked.toLocaleString("fa-IR")} از{" "}
                      {required.length.toLocaleString("fa-IR")} مرحله ضروری
                      انجام شده
                    </span>
                  </div>
                )}
              </div>
              <button
                className="amb-secondary"
                onClick={() => moveToTab("onboarding")}
              >
                مشاهده مراحل <ArrowLeft size={16} />
              </button>
            </aside>
          )}
          <nav className="amb-tabs" aria-label="بخش‌های سفیر">
            {tabs
              .filter((t) => !stopped || ["reports", "support"].includes(t.key))
              .map((t) => (
                <button
                  type="button"
                  key={t.key}
                  disabled={busy}
                  className={`amb-tab ${tab === t.key ? "amb-tab-active" : "amb-secondary"}`}
                  aria-pressed={tab === t.key}
                  aria-controls="amb-current-panel"
                  onClick={() => moveToTab(t.key)}
                >
                  <t.icon size={17} />
                  <span>{t.label}</span>
                  {t.key === "support" && openTickets > 0 && (
                    <span className="amb-count">
                      {openTickets.toLocaleString("fa-IR")}
                    </span>
                  )}
                </button>
              ))}
          </nav>
          {stopped && (
            <aside className="amb-next">
              <div>
                <h2>همکاری پایان یافته است</h2>
                <p>
                  برای پیگیری لغو دسترسی کانال‌ها یا درخواست مربوط به اطلاعات
                  خود، همچنان می‌توانید از پشتیبانی استفاده کنید.
                </p>
              </div>
            </aside>
          )}
          <div
            id="amb-current-panel"
            ref={panel}
            tabIndex={-1}
            aria-label={tabs.find((item) => item.key === tab)?.label}
            className={`amb-panel amb-panel-${tab}`}
          >
            {tab === "onboarding" && !stopped && (
              <>
                {profile.status === "RECRUITING" && (
                  <section className="amb-card amb-consent-card">
                    <span className="amb-eyebrow">شروع آگاهانه</span>
                    <h2>انتخاب با شماست</h2>
                    <p>{context?.consent}</p>
                    <label className="amb-check">
                      <input
                        type="checkbox"
                        checked={consentRead}
                        onChange={(e) => setConsentRead(e.target.checked)}
                      />
                      توضیحات را خوانده‌ام و مسئولیت‌ها را می‌دانم.
                    </label>
                    <div className="amb-row">
                      <button
                        disabled={busy || !consentRead}
                        onClick={() => run(() => ambassadors.consent(true))}
                      >
                        داوطلبانه می‌پذیرم <CheckCircle2 size={17} />
                      </button>
                      <button
                        disabled={busy}
                        className="amb-secondary"
                        onClick={() =>
                          setConfirmation({
                            title: "عدم تمایل به همکاری",
                            description:
                              "فرآیند جذب متوقف می‌شود. هیچ مسئولیتی برای ادامه فعالیت نخواهید داشت.",
                            label: "ثبت عدم تمایل",
                            action: () =>
                              run(
                                () => ambassadors.consent(false),
                                () => setTab("support"),
                              ),
                            danger: true,
                          })
                        }
                      >
                        تمایلی به همکاری ندارم
                      </button>
                    </div>
                  </section>
                )}
                <div className="amb-two-column">
                  <section className="amb-card amb-onboarding-card">
                    <div className="amb-section-title">
                      <h2>مسیر آماده‌سازی</h2>
                      <strong>
                        {checked.toLocaleString("fa-IR")} /{" "}
                        {required.length.toLocaleString("fa-IR")}
                      </strong>
                    </div>
                    <progress
                      value={checked}
                      max={required.length || 1}
                      aria-label="پیشرفت مراحل ضروری ورود"
                    />
                    <p className="amb-muted">
                      تأیید مراحل با پشتیبان است. دعوت مخاطبان فقط با رضایت
                      انجام می‌شود.
                    </p>
                    <ol className="amb-step-list">
                      {context?.onboarding.map((item, index) => {
                        const c = profile.checklist.find(
                          (check) => check.key === item.key,
                        );
                        return (
                          <li
                            key={item.key}
                            data-complete={c?.completed ? "true" : "false"}
                          >
                            <span
                              className="amb-step-number"
                              aria-label={
                                c?.completed ? "انجام شده" : "در انتظار"
                              }
                            >
                              {c?.completed
                                ? "✓"
                                : (index + 1).toLocaleString("fa-IR")}
                            </span>
                            <div>
                              <strong>{item.title}</strong>
                              <small className="amb-muted">
                                {c?.completedAt
                                  ? ambassadorDate(c.completedAt)
                                  : item.required
                                    ? "مرحله ضروری"
                                    : "هدف توسعه و سازمان‌دهی"}
                              </small>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </section>
                  <section className="amb-card amb-align-start amb-channel-card">
                    <span className="amb-eyebrow">اطلاعات شما</span>
                    <h2>کانال سفیری</h2>
                    <p className="amb-muted">
                      لینک خصوصی کانال را از تلگرام کپی کنید و تعداد اعضای واقعی
                      را وارد کنید.
                    </p>
                    <form
                      key={`${profile.channelUrl}:${profile.telegramUsername}:${profile.memberCount}`}
                      onSubmit={(e) => {
                        e.preventDefault();
                        const data = new FormData(e.currentTarget);
                        run(() =>
                          ambassadors.update({
                            telegramUsername: String(
                              data.get("telegramUsername") || "",
                            ).trim(),
                            channelUrl:
                              String(data.get("channelUrl") || "").trim() ||
                              null,
                            memberCount: Number(data.get("memberCount")),
                          }),
                        );
                      }}
                    >
                      <fieldset disabled={busy}>
                        <label>
                          شناسه تلگرام
                          <input
                            name="telegramUsername"
                            maxLength={100}
                            defaultValue={profile.telegramUsername || ""}
                            placeholder="@username"
                            dir="ltr"
                            autoComplete="off"
                          />
                        </label>
                        <label>
                          لینک خصوصی کانال
                          <input
                            name="channelUrl"
                            type="url"
                            pattern="https://.*"
                            defaultValue={profile.channelUrl || ""}
                            placeholder="https://t.me/+…"
                            dir="ltr"
                          />
                        </label>
                        <label>
                          تعداد اعضای واقعی
                          <input
                            name="memberCount"
                            type="number"
                            min={0}
                            max={100000000}
                            step={1}
                            required
                            defaultValue={profile.memberCount}
                            inputMode="numeric"
                          />
                        </label>
                        <button>ذخیره اطلاعات کانال</button>
                      </fieldset>
                    </form>
                    <div className="amb-tip">
                      ۱۰۰ عضو یک هدف رشد است؛ کیفیت همراهی و رضایت مخاطبان
                      اولویت دارد.
                    </div>
                  </section>
                </div>
                <div className="amb-resource-grid">
                  {resources.materials
                    .filter((m) => ["TRAINING", "REFERENCE"].includes(m.kind))
                    .map(resourceCard)}
                </div>
                {!resources.materials.some((m) =>
                  ["TRAINING", "REFERENCE"].includes(m.kind),
                ) && (
                  <section className="amb-card amb-empty">
                    <BookOpen size={28} />
                    <h2>راهنما و لینک‌های مرجع در راه‌اند</h2>
                    <p>
                      پشتیبان، آموزش اولیه و دسترسی دو کانال مرجع را با شما
                      هماهنگ می‌کند.
                    </p>
                    <button
                      className="amb-secondary"
                      onClick={() => moveToTab("support")}
                    >
                      ارتباط با پشتیبان
                    </button>
                  </section>
                )}
              </>
            )}
            {tab === "content" && !stopped && (
              <>
                <div className="amb-section-title">
                  <div>
                    <span className="amb-eyebrow">کتابخانه همراهی شما</span>
                    <h2>برای انتشار انتخاب کنید</h2>
                    <p className="amb-muted">
                      محتوای مناسب مخاطبان خود را انتخاب و پیش از انتشار بررسی
                      کنید.
                    </p>
                  </div>
                  <span className="amb-badge">
                    {resources.materials
                      .filter(
                        (m) => !["TRAINING", "REFERENCE"].includes(m.kind),
                      )
                      .length.toLocaleString("fa-IR")}{" "}
                    مطلب
                  </span>
                </div>
                <div className="amb-resource-grid">
                  {resources.materials
                    .filter((m) => !["TRAINING", "REFERENCE"].includes(m.kind))
                    .map(resourceCard)}
                </div>
                {!resources.materials.some(
                  (m) => !["TRAINING", "REFERENCE"].includes(m.kind),
                ) && (
                  <section className="amb-card amb-empty">
                    <Megaphone size={30} />
                    <h2>هنوز محتوایی منتشر نشده</h2>
                    <p>
                      آموزش‌های شروع و کانال‌های مرجع در بخش ورود و آموزش قرار
                      می‌گیرند.
                    </p>
                  </section>
                )}
              </>
            )}
            {tab === "missions" && !stopped && (
              <>
                <div className="amb-section-title">
                  <div>
                    <span className="amb-eyebrow">
                      با انتخاب و به سبک خودتان
                    </span>
                    <h2>فرصت‌های کوچک برای اثرگذاری</h2>
                  </div>
                  <span className="amb-badge">
                    {activeMissions.length.toLocaleString("fa-IR")} مأموریت باز
                  </span>
                </div>
                <aside className="amb-tip">
                  <ShieldCheck size={20} aria-hidden="true" />
                  <span>
                    مشارکت در همه مأموریت‌ها اختیاری است. بیان نظر واقعی و
                    ارتباط محترمانه مبنای فعالیت است.
                  </span>
                </aside>
                <div className="amb-mission-grid">
                  {resources.missions.map((m) => {
                    const expired =
                      !!m.deadline && new Date(m.deadline) < new Date();
                    return (
                      <article
                        className="amb-card amb-mission"
                        data-expired={expired}
                        key={m.id}
                      >
                        <div className="amb-section-title">
                          <span
                            className="amb-resource-icon"
                            aria-hidden="true"
                          >
                            <Compass size={22} />
                          </span>
                          <span
                            className="amb-badge"
                            data-status={expired ? "PAUSED" : "ACTIVE"}
                          >
                            {expired ? "مهلت پایان یافته" : "اختیاری"}
                          </span>
                        </div>
                        <h2>{m.title}</h2>
                        <p className="amb-pre amb-mission-objective">
                          <strong>هدف: </strong>
                          {m.objective}
                        </p>
                        <p className="amb-pre">
                          <strong>اقدام پیشنهادی و گزارش نتیجه: </strong>
                          {m.action}
                        </p>
                        <p className="amb-muted amb-mission-deadline">
                          <Clock3 size={14} aria-hidden="true" />
                          مهلت:{" "}
                          {m.deadline
                            ? ambassadorDate(m.deadline)
                            : "بدون مهلت مشخص"}
                        </p>
                        <div className="amb-row">
                          {m.url && (
                            <a
                              className="amb-button amb-secondary"
                              href={m.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              مشاهده مأموریت <ArrowLeft size={16} />
                            </a>
                          )}
                          {!expired && canReport && (
                            <button
                              onClick={() => {
                                setDraft((d) => ({ ...d, missionId: m.id }));
                                moveToTab("reports");
                              }}
                            >
                              ثبت گزارش این مأموریت <ClipboardList size={16} />
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
                {!resources.missions.length && (
                  <section className="amb-card amb-empty">
                    <Compass size={30} />
                    <h2>فعلاً مأموریت تازه‌ای ندارید</h2>
                    <p>
                      می‌توانید محتوای منتخب خود را منتشر کنید و گزارش عمومی
                      بدهید.
                    </p>
                  </section>
                )}
              </>
            )}
            {tab === "reports" && (
              <>
                {canReport && (
                  <div className="amb-report-layout">
                    <section className="amb-card amb-report-form">
                      <div className="amb-section-title">
                        <div>
                          <span className="amb-eyebrow">ثبت تجربه امروز</span>
                          <h2>گزارش کوتاه فعالیت</h2>
                        </div>
                        <span className="amb-save-note">
                          <ShieldCheck size={14} /> پیش‌نویس محفوظ است
                        </span>
                      </div>
                      <p className="amb-muted">
                        فقط موارد انجام‌شده را انتخاب کنید. اگر مانعی داشتید،
                        همان را توضیح دهید؛ لازم نیست همه گزینه‌ها تیک بخورند.
                      </p>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          run(
                            () =>
                              ambassadors.report({
                                ...draft,
                                summary: draft.summary.trim(),
                                publishedCount: Number(draft.publishedCount),
                                meaningfulInteractions: Number(
                                  draft.meaningfulInteractions,
                                ),
                                missionId: draft.missionId || undefined,
                                evidenceUrl:
                                  draft.evidenceUrl.trim() || undefined,
                              }),
                            () => setDraft(emptyDraft),
                          );
                        }}
                      >
                        <fieldset disabled={busy}>
                          <div className="amb-check-grid">
                            {context?.daily.map((item) => (
                              <label className="amb-check" key={item.key}>
                                <input
                                  type="checkbox"
                                  checked={draft.dailyChecklist.includes(
                                    item.key,
                                  )}
                                  onChange={(e) =>
                                    setDraft((d) => ({
                                      ...d,
                                      dailyChecklist: e.target.checked
                                        ? [...d.dailyChecklist, item.key]
                                        : d.dailyChecklist.filter(
                                            (k) => k !== item.key,
                                          ),
                                    }))
                                  }
                                />
                                {item.title}
                              </label>
                            ))}
                          </div>
                          <label>
                            خلاصه فعالیت یا مانع امروز{" "}
                            <span aria-hidden="true">*</span>
                            <textarea
                              name="summary"
                              required
                              maxLength={4000}
                              value={draft.summary}
                              placeholder="چه کاری انجام دادید؟ چه بازخورد یا مانعی داشتید؟"
                              onChange={(e) =>
                                setDraft((d) => ({
                                  ...d,
                                  summary: e.target.value,
                                }))
                              }
                            />
                          </label>
                          <div className="amb-grid">
                            <label>
                              تعداد مطالب منتشرشده
                              <input
                                type="number"
                                name="publishedCount"
                                min={0}
                                max={10000}
                                required
                                value={draft.publishedCount}
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    publishedCount: e.target.value,
                                  }))
                                }
                              />
                            </label>
                            <label>
                              تعامل‌های معنادار
                              <input
                                type="number"
                                name="meaningfulInteractions"
                                min={0}
                                max={100000}
                                required
                                value={draft.meaningfulInteractions}
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    meaningfulInteractions: e.target.value,
                                  }))
                                }
                              />
                            </label>
                          </div>
                          <div className="amb-grid">
                            <label>
                              مأموریت مرتبط (اختیاری)
                              <select
                                name="missionId"
                                value={draft.missionId}
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    missionId: e.target.value,
                                  }))
                                }
                              >
                                <option value="">گزارش عمومی</option>
                                {activeMissions.map((m) => (
                                  <option key={m.id} value={m.id}>
                                    {m.title}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              لینک شاهد فعالیت (اختیاری)
                              <input
                                type="url"
                                name="evidenceUrl"
                                pattern="https://.*"
                                dir="ltr"
                                value={draft.evidenceUrl}
                                placeholder="https://…"
                                onChange={(e) =>
                                  setDraft((d) => ({
                                    ...d,
                                    evidenceUrl: e.target.value,
                                  }))
                                }
                              />
                            </label>
                          </div>
                          <div className="amb-row amb-form-actions">
                            <button disabled={busy || !draft.summary.trim()}>
                              {busy ? "در حال ثبت…" : "ثبت گزارش امروز"}{" "}
                              <Send size={16} />
                            </button>
                            <span className="amb-muted">
                              تاریخ گزارش به‌صورت خودکار با ساعت تهران ثبت
                              می‌شود.
                            </span>
                          </div>
                        </fieldset>
                      </form>
                    </section>
                    <aside className="amb-card amb-report-guide">
                      <span className="amb-guide-icon">
                        <Sprout size={29} />
                      </span>
                      <span className="amb-eyebrow">برای یک گزارش مفید</span>
                      <h3>قدم‌های کوچک هم ارزشمندند</h3>
                      <p>
                        گزارش شما فرصتی برای مرور تجربه و دریافت همراهی پشتیبان
                        است.
                      </p>
                      <ul>
                        <li>
                          <CheckCircle2 size={17} />
                          <span>
                            آنچه واقعاً انجام داده‌اید را کوتاه و روشن بنویسید.
                          </span>
                        </li>
                        <li>
                          <CheckCircle2 size={17} />
                          <span>
                            واکنش مخاطبان یا مانعی که داشتید را با ما در میان
                            بگذارید.
                          </span>
                        </li>
                        <li>
                          <CheckCircle2 size={17} />
                          <span>
                            اگر به مأموریتی مربوط است، آن را از فرم انتخاب کنید.
                          </span>
                        </li>
                      </ul>
                      <div className="amb-guide-support">
                        <span className="amb-muted">همراه شما در این مسیر</span>
                        <strong>{ambassadorPerson(profile.support)}</strong>
                        <button
                          className="amb-secondary"
                          onClick={() => moveToTab("support")}
                        >
                          <MessageCircle size={16} /> گفت‌وگو با پشتیبان
                        </button>
                      </div>
                    </aside>
                  </div>
                )}
                {!canReport && !stopped && (
                  <section className="amb-card amb-empty">
                    <ClipboardList size={28} />
                    <h2>
                      {profile.status === "PAUSED"
                        ? "فعالیت شما موقتاً متوقف است"
                        : "ابتدا مراحل ورود را کامل کنید"}
                    </h2>
                    <p>
                      گزارش‌های قبلی در دسترس‌اند. برای فعال‌سازی یا رفع مانع با
                      پشتیبان هماهنگ کنید.
                    </p>
                    <button
                      className="amb-secondary"
                      onClick={() => moveToTab("support")}
                    >
                      ارتباط با پشتیبان
                    </button>
                  </section>
                )}
                <div className="amb-history-heading">
                  <span className="amb-section-icon">
                    <ClipboardList size={20} />
                  </span>
                  <div>
                    <span className="amb-eyebrow">مسیر تجربه‌های شما</span>
                    <h2>گزارش‌ها و بازخوردهای شما</h2>
                  </div>
                </div>
                <div className="amb-history-grid">
                  {profile.reports?.map((r) => (
                    <article className="amb-card amb-report-record" key={r.id}>
                      <div className="amb-section-title">
                        <h3>{ambassadorDay(r.day)}</h3>
                        <span
                          className="amb-badge"
                          data-status={r.feedback ? "ACTIVE" : "ONBOARDING"}
                        >
                          {r.feedback ? "بررسی شده" : "در انتظار بازخورد"}
                        </span>
                      </div>
                      {r.mission && (
                        <p className="amb-muted">مأموریت: {r.mission.title}</p>
                      )}
                      <p className="amb-pre">{r.summary}</p>
                      <div className="amb-report-numbers">
                        <span>
                          <Megaphone size={14} /> انتشار:{" "}
                          <strong>
                            {r.publishedCount.toLocaleString("fa-IR")}
                          </strong>
                        </span>
                        <span>
                          <MessageCircle size={14} /> تعامل معنادار:{" "}
                          <strong>
                            {r.meaningfulInteractions.toLocaleString("fa-IR")}
                          </strong>
                        </span>
                      </div>
                      {r.evidenceUrl && (
                        <a
                          href={r.evidenceUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          مشاهده شاهد فعالیت
                        </a>
                      )}
                      {r.feedback && (
                        <div className="amb-message">
                          <strong>
                            بازخورد پشتیبان · کیفیت{" "}
                            {r.quality?.toLocaleString("fa-IR")} از ۵
                          </strong>
                          <p className="amb-pre">{r.feedback}</p>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
                {!profile.reports?.length && (
                  <section className="amb-card amb-empty">
                    <ClipboardList size={28} />
                    <h3>هنوز گزارشی ثبت نشده است</h3>
                    <p>
                      با ثبت اولین گزارش، سابقه فعالیت و بازخورد پشتیبان را
                      همین‌جا می‌بینید.
                    </p>
                  </section>
                )}
                {loadMore("reports")}
              </>
            )}
            {tab === "support" && (
              <>
                {(profile.acceptedAt || stopped) && (
                  <section className="amb-card amb-support-compose">
                    <span className="amb-eyebrow">کنار شما هستیم</span>
                    <h2>
                      {stopped
                        ? "پیگیری خروج یا اطلاعات شخصی"
                        : "از پشتیبان کمک بگیرید"}
                    </h2>
                    <p className="amb-muted">
                      موضوع را کوتاه بنویسید و در توضیحات، مسئله و کمک
                      موردنیازتان را بیان کنید.
                    </p>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const form = e.currentTarget;
                        const data = new FormData(form);
                        run(
                          () =>
                            ambassadors.ticket({
                              subject: String(data.get("subject") || "").trim(),
                              body: String(data.get("body") || "").trim(),
                            }),
                          () => form.reset(),
                        );
                      }}
                    >
                      <fieldset disabled={busy}>
                        <label>
                          موضوع درخواست
                          <input
                            name="subject"
                            required
                            maxLength={200}
                            placeholder={
                              stopped
                                ? "مثلاً پیگیری حذف دسترسی کانال"
                                : "مثلاً مشکل دسترسی به کانال مرجع"
                            }
                          />
                        </label>
                        <label>
                          توضیحات
                          <textarea name="body" required maxLength={4000} />
                        </label>
                        <button>
                          ارسال برای پشتیبان <Send size={16} />
                        </button>
                      </fieldset>
                    </form>
                  </section>
                )}
                {!profile.acceptedAt && !stopped && (
                  <section className="amb-card amb-empty">
                    <LifeBuoy size={28} />
                    <h2>پرسشی درباره شروع همکاری دارید؟</h2>
                    <p>پیش از پذیرش، پرسش‌های خود را با مسئول جذب مطرح کنید.</p>
                    <Link className="amb-button amb-secondary" to="/contact">
                      ارتباط با مجموعه
                    </Link>
                  </section>
                )}
                {profile.tickets?.map((t) => (
                  <article className="amb-card amb-ticket" key={t.id}>
                    <div className="amb-section-title">
                      <h2>{t.subject}</h2>
                      <span
                        className="amb-badge"
                        data-status={t.resolvedAt ? "ACTIVE" : "ONBOARDING"}
                      >
                        {t.resolvedAt ? "رسیدگی شده" : "در حال پیگیری"}
                      </span>
                    </div>
                    <div className="amb-conversation">
                      {t.replies.map((r) => (
                        <div
                          className={`amb-message ${r.actorId === user.id ? "amb-message-own" : ""}`}
                          key={r.id}
                        >
                          <small>
                            {r.actorId === user.id ? "شما" : "پشتیبان"} ·{" "}
                            {ambassadorDate(r.createdAt)}
                          </small>
                          <p className="amb-pre">{r.body}</p>
                        </div>
                      ))}
                    </div>
                    <form
                      className="amb-reply-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const form = e.currentTarget;
                        const data = new FormData(form);
                        run(
                          () =>
                            ambassadors.reply(
                              t.id,
                              String(data.get("body") || "").trim(),
                            ),
                          () => form.reset(),
                        );
                      }}
                    >
                      <label>
                        ادامه گفت‌وگو
                        <textarea
                          name="body"
                          required
                          maxLength={4000}
                          placeholder={
                            t.resolvedAt
                              ? "ارسال پاسخ، این درخواست را دوباره باز می‌کند."
                              : "پیام شما برای پشتیبان…"
                          }
                        />
                      </label>
                      <button disabled={busy} className="amb-secondary">
                        ارسال پاسخ
                      </button>
                    </form>
                  </article>
                ))}
                {!profile.tickets?.length && (
                  <p className="amb-muted">
                    درخواست‌های شما و پاسخ‌های پشتیبان در همین بخش نمایش داده
                    می‌شوند.
                  </p>
                )}
                {loadMore("tickets")}
              </>
            )}
          </div>
          {!stopped && (
            <details className="amb-card amb-exit">
              <summary>مدیریت ادامه همکاری</summary>
              <p>
                هر زمان بخواهید می‌توانید انصراف دهید. سوابق شما برای پیگیری
                داخلی حفظ می‌شوند و پشتیبان لغو دسترسی کانال‌ها را پیگیری
                می‌کند.
              </p>
              <button
                disabled={busy}
                className="amb-secondary"
                onClick={() =>
                  setConfirmation({
                    title: "انصراف از برنامه سفیران",
                    description:
                      "دسترسی ثبت فعالیت و محتوای برنامه بسته می‌شود. درخواست‌های پشتیبانی و پیگیری خروج در دسترس می‌مانند.",
                    label: "تأیید انصراف",
                    danger: true,
                    action: async () => {
                      const ok = await run(
                        () => ambassadors.withdraw(),
                        () => setDraft(emptyDraft),
                      );
                      if (ok) setTab("support");
                    },
                  })
                }
              >
                درخواست پایان همکاری
              </button>
            </details>
          )}
        </>
      )}
      <ConfirmDialog
        confirmation={confirmation}
        busy={busy}
        onClose={() => setConfirmation(null)}
      />
    </main>
  );
}
